import { useState, type ReactNode } from 'react'
import { ThumbsUpIcon } from '@systemeio/icons'
import { twMerge } from 'tailwind-merge'
import type { RequireAtLeastOne } from 'shared/types/at-least-one'
import type { OnErrorType } from 'modules/comments/types'
import { useHover } from '../../hooks/use-hover'
import { LikeDetailPopover } from './like-detail-popover'

interface LikeSection {
  className?: string
  isLiked: boolean
  likesCount: number

  postId: number
  commentId: number

  readOnly?: boolean
  handleLikeClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  onError?: OnErrorType
}

export type LikeSectionProps = RequireAtLeastOne<LikeSection, 'postId' | 'commentId'>

export const LikeButton = ({
  className,
  isLiked,
  likesCount,
  postId,
  commentId,
  readOnly,
  handleLikeClick,
}: LikeSectionProps): ReactNode => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const { hovered, ref: hoverRef } = useHover()

  return (
    <>
      <button
        type="button"
        disabled={readOnly}
        onClick={handleLikeClick}
        className={twMerge(
          `group relative inline-flex items-center gap-2 rounded-md px-1.5 py-2 text-text-tertiary *:transition-colors hover:bg-bg-accent/20`,
          className,
        )}
      >
        <ThumbsUpIcon
          data-liked={isLiked}
          className="size-5 text-text-secondary group-hover:text-text-link data-[liked=true]:text-text-link"
        />
        {likesCount > 0 && (
          <div ref={readOnly ? undefined : hoverRef} className="flex">
            <span ref={readOnly ? undefined : setReferenceElement}>{likesCount}</span>
          </div>
        )}
      </button>
      {!readOnly && hovered && (
        <LikeDetailPopover
          postId={postId}
          commentId={commentId}
          likesCount={likesCount}
          referenceElement={referenceElement}
        />
      )}
    </>
  )
}
